import { useState, memo, useCallback, useEffect, useRef } from "react";

function ConfirmDialog({ isOpen, onConfirm, onCancel, message }) {
  if (!isOpen) return null;

  return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md">
          <h3 className="font-semibold text-lg mb-3">Potvrzení</h3>
          <p className="mb-6 text-gray-700">{message}</p>
          <div className="flex gap-3 justify-end">
            <button
                onClick={onCancel}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition"
            >
              Zrušit
            </button>
            <button
                onClick={onConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
            >
              Smazat
            </button>
          </div>
        </div>
      </div>
  );
}

function SuccessMessage({ isOpen, onClose, message }) {
  if (!isOpen) return null;

  return (
      <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md">
          <div className="flex items-center mb-3">
            <span className="text-3xl mr-3">✅</span>
            <h3 className="font-semibold text-lg">Úspěch</h3>
          </div>
          <p className="mb-6 text-gray-700 whitespace-pre-line">{message}</p>
          <div className="flex justify-end">
            <button
                onClick={onClose}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
            >
              OK
            </button>
          </div>
        </div>
      </div>
  );
}

const DraggableItem = memo(({ item, source }) => {
  const handleDragStart = useCallback((e) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", JSON.stringify({ source, item }));
  }, [source, item]);

  return (
      <div
          draggable
          onDragStart={handleDragStart}
          className="p-2 mb-1 rounded cursor-grab hover:bg-gray-100 bg-white border text-sm"
      >
        {item.name}
      </div>
  );
});

DraggableItem.displayName = 'DraggableItem';

const DroppableMaster = memo(({ item, onMap, mappedItems, onUnmap, onDelete, isCustom }) => {
  const [isOver, setIsOver] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setIsOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsOver(false);
    const data = JSON.parse(e.dataTransfer.getData("text/plain"));
    onMap(data.source, data.item, item.id);
  }, [onMap, item.id]);

  const handleDelete = useCallback(() => {
    onDelete(item.id);
  }, [onDelete, item.id]);

  const itemsForThisMaster = mappedItems.filter(m => m.masterId === item.id);

  return (
      <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`p-2 mb-2 rounded ${
              isOver ? "bg-blue-200 border-2 border-blue-400" : "hover:bg-gray-100 border border-gray-300"
          }`}
      >
        <div className="flex justify-between items-center mb-1">
          <div className="font-medium text-sm">
            {item.name}
            {isCustom && <span className="ml-2 text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded">vlastní</span>}
          </div>
          {isCustom && (
              <button
                  onClick={handleDelete}
                  className="text-red-600 hover:text-red-800 font-bold text-lg"
                  title="Smazat vlastní kategorii"
              >
                ×
              </button>
          )}
        </div>
        {itemsForThisMaster.length > 0 && (
            <div className="ml-3 mt-1 space-y-1">
              {itemsForThisMaster.map((mapped, idx) => (
                  <div key={idx} className="text-xs bg-green-100 p-1 rounded border border-green-300 flex justify-between items-center">
                    <span>↳ {mapped.itemName} <span className="text-gray-500">({mapped.source})</span></span>
                    <button
                        onClick={() => onUnmap(mapped.source, mapped.itemId)}
                        className="ml-2 text-red-600 hover:text-red-800 font-bold"
                        title="Odebrat párování"
                    >
                      ×
                    </button>
                  </div>
              ))}
            </div>
        )}
      </div>
  );
});

DroppableMaster.displayName = 'DroppableMaster';

export default function CategoryMappingEditor() {
  const [source1, setSource1] = useState([]);
  const [source2, setSource2] = useState([]);
  const [mapping, setMapping] = useState([]);
  const [searchMaster, setSearchMaster] = useState("");
  const [searchSource, setSearchSource] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [customCategories, setCustomCategories] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, categoryId: null });
  const [successMessage, setSuccessMessage] = useState({ isOpen: false, message: "" });
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastAutoSave, setLastAutoSave] = useState(null);

  // Pro sledování změn
  const lastSavedState = useRef(null);
  const autoSaveTimer = useRef(null);

  // Detekce změn v datech
  const hasChanges = useCallback(() => {
    if (!lastSavedState.current) return true;

    const currentState = JSON.stringify({
      source1,
      source2,
      mapping,
      customCategories
    });

    return currentState !== lastSavedState.current;
  }, [source1, source2, mapping, customCategories]);

  // Automatické ukládání
  const autoSave = useCallback(() => {
    if (!autoSaveEnabled) return;
    if (!hasChanges()) return;
    if (source1.length === 0 && source2.length === 0 && customCategories.length === 0) return;

    const state = {
      stateFile: true,
      version: "1.0",
      source1,
      source2,
      mapping,
      customCategories,
      savedAt: new Date().toISOString(),
      autoSaved: true
    };

    // Uložit do localStorage
    try {
      localStorage.setItem('categoryMappingAutoSave', JSON.stringify(state));
      lastSavedState.current = JSON.stringify({
        source1,
        source2,
        mapping,
        customCategories
      });
      setLastAutoSave(new Date());
      console.log('Automaticky uloženo do localStorage');
    } catch (err) {
      console.error('Chyba při automatickém ukládání:', err);
    }
  }, [source1, source2, mapping, customCategories, autoSaveEnabled, hasChanges]);

  // Nastavení časovače pro automatické ukládání
  useEffect(() => {
    if (autoSaveEnabled) {
      autoSaveTimer.current = setInterval(() => {
        autoSave();
      }, 1* 60 * 1000); // 1 minut

      return () => {
        if (autoSaveTimer.current) {
          clearInterval(autoSaveTimer.current);
        }
      };
    }
  }, [autoSave, autoSaveEnabled]);

  // Načíst automaticky uložený stav při startu
  useEffect(() => {
    try {
      const saved = localStorage.getItem('categoryMappingAutoSave');
      if (saved) {
        const state = JSON.parse(saved);
        // Nenačítat automaticky, jen informovat uživatele
        if (state.autoSaved && (source1.length === 0 && source2.length === 0)) {
          const shouldLoad = window.confirm(
              `Nalezen automaticky uložený stav z ${new Date(state.savedAt).toLocaleString('cs-CZ')}.\n\nChcete ho načíst?`
          );

          if (shouldLoad) {
            setSource1(state.source1 || []);
            setSource2(state.source2 || []);
            setMapping(state.mapping || []);
            const loadedCustomCategories = (state.customCategories || []).map(cat => ({
              ...cat,
              isCustom: true
            }));
            setCustomCategories(loadedCustomCategories);

            lastSavedState.current = JSON.stringify({
              source1: state.source1,
              source2: state.source2,
              mapping: state.mapping,
              customCategories: state.customCategories
            });
          }
        }
      }
    } catch (err) {
      console.error('Chyba při načítání automaticky uloženého stavu:', err);
    }
  }, []); // Spustit pouze při prvním načtení

  const handleMap = useCallback((source, item, masterId) => {
    setMapping(prev => [...prev, {
      source,
      itemId: item.id,
      itemName: item.name,
      masterId
    }]);
  }, []);

  const handleUnmap = useCallback((source, itemId) => {
    setMapping(prev => prev.filter(m => !(m.source === source && m.itemId === itemId)));
  }, []);

  const handleAddCustomCategory = useCallback(() => {
    if (!newCategoryName.trim()) {
      alert('Zadejte název kategorie');
      return;
    }

    const newId = `custom_${Date.now()}`;
    const newCategory = {
      id: newId,
      name: newCategoryName.trim(),
      isCustom: true
    };

    setCustomCategories(prev => [...prev, newCategory]);
    setNewCategoryName("");

    setSuccessMessage({
      isOpen: true,
      message: `Kategorie "${newCategory.name}" byla úspěšně přidána!`
    });
  }, [newCategoryName]);

  const handleDeleteCustomCategory = useCallback((categoryId) => {
    setDeleteConfirm({ isOpen: true, categoryId });
  }, []);

  const confirmDelete = useCallback(() => {
    const { categoryId } = deleteConfirm;

    setCustomCategories(prev => prev.filter(cat => cat.id !== categoryId));
    setMapping(prev => prev.filter(m => m.masterId !== categoryId));

    setDeleteConfirm({ isOpen: false, categoryId: null });
  }, [deleteConfirm]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ isOpen: false, categoryId: null });
  }, []);

  const handleFileUpload = (e, sourceType) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target.result);
        if (Array.isArray(json)) {
          if (sourceType === 'master') {
            const cleanedData = json.map(item => ({
              id: item.id,
              name: item.name
            }));
            setSource1(cleanedData);
          } else {
            setSource2(json);
          }
        } else {
          alert('JSON musí být pole objektů s polemi "id" a "name"');
        }
      } catch (err) {
        alert('Chyba při parsování JSON: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  const handleSaveState = () => {
    const state = {
      stateFile: true,
      version: "1.0",
      source1,
      source2,
      mapping,
      customCategories,
      savedAt: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(state, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const filename = `mapping-state-${new Date().toISOString().split('T')[0]}.json`;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);

    // Aktualizovat lastSavedState
    lastSavedState.current = JSON.stringify({
      source1,
      source2,
      mapping,
      customCategories
    });

    setSuccessMessage({
      isOpen: true,
      message: `Stav práce byl uložen do souboru:\n"${filename}"\n\nTento soubor můžete později načíst pro pokračování v práci.`
    });
  };

  const handleLoadState = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const state = JSON.parse(event.target.result);

        if (state.stateFile === true || (state.source1 !== undefined && state.source2 !== undefined && state.mapping !== undefined)) {
          setSource1(state.source1 || []);
          setSource2(state.source2 || []);
          setMapping(state.mapping || []);

          const loadedCustomCategories = (state.customCategories || []).map(cat => ({
            ...cat,
            isCustom: true
          }));
          setCustomCategories(loadedCustomCategories);

          // Aktualizovat lastSavedState
          lastSavedState.current = JSON.stringify({
            source1: state.source1,
            source2: state.source2,
            mapping: state.mapping,
            customCategories: state.customCategories
          });

          setSuccessMessage({
            isOpen: true,
            message: `Stav úspěšně načten!\n\nMaster kategorie: ${state.source1?.length || 0}\nZdrojové kategorie: ${state.source2?.length || 0}\nVlastní kategorie: ${loadedCustomCategories.length}\nMapování: ${state.mapping?.length || 0}`
          });
        } else {
          alert('Neplatný formát souboru se stavem.\n\nOčekává se soubor "mapping-state-*.json" s poli: source1, source2, mapping, customCategories\n\nNačtený soubor obsahuje: ' + Object.keys(state).join(', '));
        }
      } catch (err) {
        alert('Chyba při načítání stavu: ' + err.message);
      }
    };
    reader.readAsText(file);

    e.target.value = null;
  };

  const handleExportMapping = () => {
    const allMasterCategories = [...source1, ...customCategories];
    const exportData = allMasterCategories.map(master => {
      const mappedToThis = mapping.filter(m => m.masterId === master.id);
      return {
        masterId: master.id,
        masterName: master.name,
        isCustom: master.isCustom || false,
        mappedItems: mappedToThis.map(m => ({
          itemId: m.itemId,
          itemName: m.itemName,
          source: m.source
        }))
      };
    });

    const fullExport = {
      exportType: "category-mapping",
      exportedAt: new Date().toISOString(),
      categories: exportData,
      summary: {
        totalMasterCategories: allMasterCategories.length,
        standardCategories: source1.length,
        customCategories: customCategories.length,
        totalMappings: mapping.length,
        categoriesWithMappings: exportData.filter(c => c.mappedItems.length > 0).length
      }
    };

    const blob = new Blob([JSON.stringify(fullExport, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const filename = `category-mapping-export-${new Date().toISOString().split('T')[0]}.json`;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);

    setSuccessMessage({
      isOpen: true,
      message: `Finální mapování bylo exportováno do souboru:\n"${filename}"\n\nTento soubor obsahuje kompletní mapování všech kategorií.`
    });
  };

  const mappedIds = new Set(mapping.map(m => `${m.source}:${m.itemId}`));
  const availableSource2 = source2.filter(item => !mappedIds.has(`source2:${item.id}`));

  const removeDiacritics = (str) => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  };

  const allMasterCategories = [...source1, ...customCategories];
  const filteredMaster = allMasterCategories.filter(item =>
      removeDiacritics(item.name).includes(removeDiacritics(searchMaster))
  );

  const filteredSource = availableSource2.filter(item =>
      removeDiacritics(item.name).includes(removeDiacritics(searchSource))
  );

  return (
      <div className="p-4">
        <ConfirmDialog
            isOpen={deleteConfirm.isOpen}
            onConfirm={confirmDelete}
            onCancel={cancelDelete}
            message="Opravdu chcete smazat tuto vlastní kategorii a všechna její mapování?"
        />

        <SuccessMessage
            isOpen={successMessage.isOpen}
            onClose={() => setSuccessMessage({ isOpen: false, message: "" })}
            message={successMessage.message}
        />

        <div className="mb-4 p-3 bg-blue-50 rounded">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-semibold">Nahrát data:</h3>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                    type="checkbox"
                    checked={autoSaveEnabled}
                    onChange={(e) => setAutoSaveEnabled(e.target.checked)}
                    className="w-4 h-4"
                />
                <span>Automatické ukládání v prohlížeči (každou 1 min)</span>
              </label>
              {lastAutoSave && autoSaveEnabled && (
                  <span className="text-xs text-gray-600">
                  Poslední: {lastAutoSave.toLocaleTimeString('cs-CZ')}
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-4 mb-3">
            <div>
              <label className="block text-sm mb-1">Master kategorie (JSON):</label>
              <input
                  type="file"
                  accept=".json"
                  onChange={(e) => handleFileUpload(e, 'master')}
                  className="text-sm"
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Zdrojové kategorie (JSON):</label>
              <input
                  type="file"
                  accept=".json"
                  onChange={(e) => handleFileUpload(e, 'source')}
                  className="text-sm"
              />
            </div>
          </div>

          <div className="flex gap-4 mb-3 border-t pt-3">
            <div>
              <label className="block text-sm mb-1 font-semibold text-green-700">📥 Načíst uložený stav práce:</label>
              <input
                  type="file"
                  accept=".json"
                  onChange={handleLoadState}
                  className="text-sm"
              />
              <p className="text-xs text-gray-600 mt-1">Soubor "mapping-state-*.json"</p>
            </div>
            <div className="flex items-end">
              <button
                  onClick={handleSaveState}
                  disabled={source1.length === 0 && source2.length === 0 && customCategories.length === 0}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
              >
                💾 Uložit stav práce
              </button>
            </div>
          </div>

          <div className="flex justify-between items-center border-t pt-3">
            <p className="text-sm">
              Master: {source1.length} | Vlastní: {customCategories.length} | Mapování: {mapping.length}
              {hasChanges() && <span className="ml-2 text-orange-600">• Neuložené změny</span>}
            </p>
            <button
                onClick={handleExportMapping}
                disabled={mapping.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              📤 Export mapování (finální)
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 h-[65vh]">
          <div className="border rounded-2xl overflow-hidden bg-gray-50 flex flex-col">
            <div className="bg-gray-50 border-b border-gray-200">
              <h2 className="font-semibold p-3 pb-2">
                Master kategorie ({filteredMaster.length}/{allMasterCategories.length})
              </h2>

              <div className="mx-3 mb-2 p-2 bg-purple-50 rounded border border-purple-200">
                <label className="block text-xs font-semibold mb-1 text-purple-800">
                  Přidat vlastní kategorii:
                </label>
                <div className="flex gap-2">
                  <input
                      type="text"
                      placeholder="Název kategorie..."
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddCustomCategory();
                        }
                      }}
                      className="flex-1 p-2 border rounded text-sm"
                  />
                  <button
                      onClick={handleAddCustomCategory}
                      className="px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm whitespace-nowrap"
                  >
                    + Přidat
                  </button>
                </div>
              </div>

              <div className="px-3 pb-3">
                <input
                    type="text"
                    placeholder="Hledat v master kategoriích..."
                    value={searchMaster}
                    onChange={(e) => setSearchMaster(e.target.value)}
                    className="w-full p-2 border rounded text-sm bg-white"
                />
              </div>
            </div>

            <div className="overflow-auto flex-1 p-3">
              {allMasterCategories.length === 0 ? (
                  <p className="text-sm text-gray-500">Nahrajte JSON soubor s master kategoriemi nebo přidejte vlastní</p>
              ) : filteredMaster.length === 0 ? (
                  <p className="text-sm text-gray-500">Žádné výsledky</p>
              ) : (
                  filteredMaster.map(item => (
                      <DroppableMaster
                          key={item.id}
                          item={item}
                          onMap={handleMap}
                          mappedItems={mapping}
                          onUnmap={handleUnmap}
                          onDelete={handleDeleteCustomCategory}
                          isCustom={item.isCustom || false}
                      />
                  ))
              )}
            </div>
          </div>

          <div className="border rounded-2xl overflow-hidden bg-gray-50 flex flex-col">
            <div className="bg-gray-50 border-b border-gray-200">
              <h2 className="font-semibold p-3 pb-2">
                Zdrojové kategorie ({filteredSource.length}/{availableSource2.length})
              </h2>
              <div className="px-3 pb-3">
                <input
                    type="text"
                    placeholder="Hledat ve zdrojových kategoriích..."
                    value={searchSource}
                    onChange={(e) => setSearchSource(e.target.value)}
                    className="w-full p-2 border rounded text-sm bg-white"
                />
              </div>
            </div>

            <div className="overflow-auto flex-1 p-3">
              {source2.length === 0 ? (
                  <p className="text-sm text-gray-500">Nahrajte JSON soubor se zdrojovými kategoriemi</p>
              ) : filteredSource.length === 0 ? (
                  <p className="text-sm text-gray-500">Žádné výsledky</p>
              ) : (
                  filteredSource.map(item => (
                      <DraggableItem key={item.id} item={item} source="source2" />
                  ))
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-gray-100 rounded text-xs">
          <strong>Formát JSON:</strong> [{"{"}"id": "1", "name": "Kategorie 1"{"}"}, {"{"}"id": "2", "name": "Kategorie 2"{"}"}]
          <br />
          <strong className="mt-2 block">Rozdíl mezi soubory:</strong>
          <ul className="list-disc ml-5 mt-1 text-gray-600">
            <li><strong>mapping-state-*.json</strong> (zelené tlačítko 💾) = obsahuje VŠECHNA data pro pokračování v práci (source1, source2, mapping, customCategories)</li>
            <li><strong>category-mapping-export-*.json</strong> (modré tlačítko 📤) = finální výstup s mapováním kategorií (categories, summary)</li>
            <li><strong>Automatické ukládání</strong> = ukládá se do localStorage každou 1 minutu (pouze pokud jsou změny)</li>
          </ul>
        </div>
      </div>
  );
}